import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography, Avatar, Skeleton, Button, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BasicVideoPlayer from './BasicVideoPlayer';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const getPostId = (url) => {
  if (!url) return null;
  const m = url.match(/comments\/([a-z0-9]+)/i);
  return m ? m[1] : null;
};

const TopComments = ({ redditUrl }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const postId = getPostId(redditUrl);
    if (!postId) { setLoading(false); return; }
    setLoading(true);
    setComments([]);

    fetch(`${API_URL}/api/reddit-comments/${postId}/`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setComments(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [redditUrl]);

  if (!loading && comments.length === 0) return null;

  return (
    <Box>
      {loading ? (
        [0,1,2,3,4].map(i => (
          <Box key={i} sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
              <Skeleton variant="text" width="40%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
            </Box>
            <Skeleton variant="text" width="95%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
            <Skeleton variant="text" width="80%" sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
          </Box>
        ))
      ) : (
        comments.map((c, i) => (
          <Box
            key={c.id}
            sx={{
              mb: i < comments.length - 1 ? 2.5 : 2,
              pb: i < comments.length - 1 ? 2.5 : 0,
              borderBottom: i < comments.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>
                {c.author ? c.author[0].toUpperCase() : '?'}
              </Avatar>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, fontSize: '0.68rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                u/{c.author}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}>
                <ArrowUpwardIcon sx={{ fontSize: 10, color: 'primary.main' }} />
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.65rem' }}>
                  {c.score >= 1000 ? `${(c.score/1000).toFixed(1)}k` : c.score}
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.78rem',
                lineHeight: 1.55,
              }}
            >
              {c.body}
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
};

const BasicVideoModal = ({ open, onClose, nomination }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showComments, setShowComments] = React.useState(false);

  React.useEffect(() => {
    setShowComments(open ? isMobile : false);
  }, [open, isMobile]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: '#141414',
          borderRadius: isMobile ? 0 : 2,
          border: isMobile ? 'none' : '1px solid rgba(255, 255, 255, 0.08)',
          overflow: 'hidden',
          ...(isMobile ? { display: 'flex', flexDirection: 'column' } : {}),
        }
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: isMobile ? 8 : 8,
          color: 'common.white',
          zIndex: 10,
          backgroundColor: 'rgba(0,0,0,0.5)',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0, bgcolor: '#141414', overflow: 'hidden', ...(isMobile ? { flex: 1, display: 'flex', flexDirection: 'column' } : {}) }}>
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          height: isMobile ? '100%' : '75vh',
          minHeight: isMobile ? 'auto' : 400,
          overflow: 'hidden',
        }}>
          {/* Video — pinned at top on mobile, fills left on desktop */}
          <Box sx={{
            ...(isMobile
              ? { width: '100%', height: '55vh', flexShrink: 0, bgcolor: '#000' }
              : { flex: '1 1 0', minWidth: 0, bgcolor: '#000', display: 'flex', alignItems: 'center' }
            ),
          }}>
            <Box sx={{ width: '100%', height: '100%' }}>
              <BasicVideoPlayer
                redditUrl={nomination?.reddit_url}
                s3VideoUrl={nomination?.s3_video_url}
                thumbnailUrl={nomination?.thumbnail_url}
                videoUrl={nomination?.video_url}
                autoPlay={true}
                controls={true}
                isMobile={isMobile}
              />
            </Box>
          </Box>

          {/* Mobile: bottom info box fills remaining space and scrolls */}
          {isMobile && (
            <>
              {/* Bottom box: nomination title + toggle + comments */}
              <Box sx={{ flex: 1, minHeight: 0, bgcolor: '#141414', borderTop: '1px solid rgba(255,255,255,0.08)', overflowY: 'auto', overflowX: 'hidden' }}>
              {nomination?.match && (
                <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                    Nomination
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.4, mt: 0.5 }}>
                    {nomination.match}
                  </Typography>
                </Box>
              )}
              {nomination?.reddit_url && (
                <Box sx={{ px: 2, pb: showComments ? 1 : 3 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setShowComments(v => !v)}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.15)',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' },
                    }}
                  >
                    {showComments ? 'Hide comments' : 'View top comments'}
                  </Button>
                </Box>
              )}
              {nomination?.reddit_url && showComments && (
                <Box sx={{ px: 2, pb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 2 }}>
                    <Box sx={{ width: 16, height: 2, bgcolor: 'primary.main', flexShrink: 0 }} />
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.58rem', letterSpacing: '0.14em' }}>
                      Top comments
                    </Typography>
                  </Box>
                  <TopComments redditUrl={nomination.reddit_url} />
                </Box>
              )}
            </Box>
            </>
          )}

          {/* Desktop: right panel */}
          {!isMobile && nomination?.reddit_url && (
            <Box sx={{
              width: 300,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              overflowY: 'auto',
              bgcolor: '#1A1A1A',
            }}>
              {nomination?.match && (
                <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600 }}>
                    Nomination
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.4, mt: 0.5 }}>
                    {nomination.match}
                  </Typography>
                </Box>
              )}
              <Box sx={{ px: 2, pt: 2, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{ width: 16, height: 2, bgcolor: 'primary.main', flexShrink: 0 }} />
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.58rem', letterSpacing: '0.14em' }}>
                    Top comments
                  </Typography>
                </Box>
                <TopComments redditUrl={nomination.reddit_url} />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BasicVideoModal;
